import React, {useEffect, useState} from 'react';
import {AppInterface, InvalidPanel, PageLinkSharePanel, ThemedButton, ThemedDialog, ThemedDrawer, ThemedIconButton} from "../../index";
import {useTheme} from "../../../common/ProjectThemeProvider";
import {checkNullStr, getJsonValue, isBoolTrue, setTabTitle} from "../../../common/fusion-ui/utils/ReactUtils";
import {isCurrUserRoleAdmin, printError, printLog} from "../../utils/AppUtils";
import {useToast} from "../../../common/fusion-ui/provider/ToastProvider";
import {Flexbox} from "../../../common/fusion-ui";
import {Keys, ModuleRouteUrls, ShowViewType} from "../../config/AppConfig";
import {useConfig} from "../../context/ConfigProvider";
import useScreenSize from "../../../common/fusion-ui/hook/useScreenSize";

/**
 * @example
 *  <InternalPageRoot
 *     tag={TAG}
 *     moduleName={moduleName}
 *     title={`Document Details`}
 *     showAs={ShowViewType.PAGE}
 *     isDataFound={getField(Fields.isDataFound)}
 *     isDataLoading={getField(Fields.isDataLoading)}
 *     dataLoadingMsg={getField(Fields.dataLoadingMsg)}
 *     backToListText={`Back to list`}
 *     internalPageUrl={GenRouteUrls.doc_internalPage}
 *     prevRecordId={getField(Fields.prevRecordId)}
 *     nextRecordId={getField(Fields.nextRecordId)}
 *     showControls={true}
 *     showPrev={true}
 *     showNext={true}
 *     onPrev={() => fetchPageData(getField(Fields.prevRecordId), false)}
 *     onNext={() => fetchPageData(getField(Fields.nextRecordId), false)}>
 *     <div>
 *         children
 *     </div>
 *  </InternalPageRoot>
 */
const InternalPageRoot = React.memo(({
                                         tag,
                                         moduleName,
                                         title,
                                         isDataFound,
                                         isDataLoading,
                                         dataLoadingMsg,
                                         children,
                                         freeComponent,
                                         backToListText = `Back to list`,
                                         showControls = true,
                                         showPrev = true,
                                         showNext = true,
                                         showBack = true,
                                         showShare = false,
                                         prevRecordId,
                                         nextRecordId,
                                         internalPageUrl,
                                         shareUrl,
                                         onBackToList,
                                         onPrev,
                                         onNext,
                                         showHeader = true,
                                         closeOnOutsideClick = true,
                                         closeOnEscape = true,

                                         drawerWidth = '60%',
                                         dialogMd = 8,

                                         open,
                                         onHide,
                                         showAs = ShowViewType.PAGE,
                                         popupProps = {},
                                     }) => {
    const TAG = "InternalPageRoot";

    const theme = useTheme();
    const {showToast, changeToastVariant} = useToast();
    const {isConfigTrue, updateConfig, getConfig} = useConfig();
    const {isScreenUpSm} = useScreenSize();

    const Fields = {
        showShareDialog: 'showShareDialog',
    };

    const [fieldsData, setFieldsData] = useState({});

    useEffect(() => {
        if (showAs == ShowViewType.PAGE) {
            setTabTitle(title);
            updateConfig(Keys.config_ShowTabBar, true);
            updateConfig(Keys.config_TabBarTab, -1);
            // const recordId = getCurrUrlParamValue('id');
        }
        updateConfig(Keys.tracker_currPageTag, tag);
        updateConfig(Keys.tracker_currPageModule, moduleName);
    }, [showAs, moduleName]);


    return (<>
        {createViewAsPanel()}
        {canShowShareButton() && createSharePanel()}
    </>);

    function createViewAsPanel() {
        if (showAs == ShowViewType.DRAWER && isScreenUpSm) {
            // return createDialog();
        }
        switch (showAs) {
            case ShowViewType.PAGE:
                return createPage();
            case ShowViewType.PANEL:
                return createPanel();
            case ShowViewType.DIALOG:
                return createDialog();
            case ShowViewType.DRAWER:
                return createDrawer();
        }
    }

    function createDialog() {
        return <ThemedDialog
            widthClass={`col-sm-10 col-md-${dialogMd}`}
            className={"rounded-5 p-0 m-0 "}
            titleClassName={'fs-lg'}
            contentClassName={'pb-3 mb-3x mt-2 px-4'}
            maxHeight={'85vh'}
            open={open}
            title={title}
            showClose={true}
            showFooter={false}
            onHide={onHide}
            onClose={onHide}
            onEscapeClose={onHide}
            onOutsideClick={() => {
                if (!isBoolTrue(closeOnEscape)) {
                    if (onHide) onHide();
                }
            }}
            closeOnEscape={closeOnEscape}
            closeOnOutsideClick={closeOnOutsideClick}
            {...popupProps}
        >
            <div className={'mt-n2'}>
                {createPanel()}
            </div>
        </ThemedDialog>
        // return <RootInternalDialog
        //     open={open}
        //     title={title}
        //     onHide={onHide}
        //     closeOnEscape={closeOnEscape}
        //     closeOnOutsideClick={closeOnOutsideClick}
        //     md={dialogMd}
        //     {...popupProps}>
        //     <div className={'mt-n2'}>
        //         {createPanel()}
        //     </div>
        // </RootInternalDialog>
    }

    function createDrawer() {
        return <ThemedDrawer
            title={title}
            open={open}
            width={drawerWidth}
            onClose={onHide}
            closeOnEscape={closeOnEscape}
            titleAt={'start'}
            titleClassName={'px-2 fs-lg'}
            closeOnClickAway={closeOnOutsideClick}
            headerContent={<div className={'me-4'}>
                {createHeaderControls()}
            </div>}
            {...popupProps}>
            {createPanel()}
        </ThemedDrawer>
    }

    function createPage() {
        return <AppInterface
            label={title}>
            {createPanel()}
        </AppInterface>
    }

    function createPanel() {
        return <div className={'w-100'}>
            <div className={'w-100'}>
                <div className={'position-relative'}>
                    {freeComponent && freeComponent}
                </div>

                <InvalidPanel
                    color={theme.text70}
                    success={isDataFound}
                    loading={isDataLoading}
                    msg={dataLoadingMsg}
                    show404Image={true}
                />

                {(!isBoolTrue(isDataLoading) && isBoolTrue(isDataFound)) &&
                    <div>
                        {/*header*/}
                        {isBoolTrue(showHeader) &&
                            <div className={`${isScreenUpSm ? 'position-relative' : 'position-absolute'}`}
                                 style={{
                                     top: (showAs == ShowViewType.DRAWER || showAs == ShowViewType.DIALOG) ? '0rem' : '.2rem',
                                     right: '.82rem',
                                 }}>
                                {showAs != ShowViewType.DRAWER && createHeaderControls()}
                            </div>}

                        <div className={`m-0 p-0 px-1 w-100x`}>
                            {children}
                        </div>
                    </div>}
            </div>
        </div>
    }

    function createHeaderControls() {
        return isBoolTrue(showControls) &&
            <Flexbox className={isScreenUpSm ? 'px-2 mb-2 mt-n2' : ``}
                     justifyAt={isScreenUpSm ? 'start' : 'end'} direction={'row'}>
                {/* Back to list button*/}
                {(showAs == ShowViewType.PAGE && isBoolTrue(showBack)) && <ThemedButton
                    className={'px-2 py-1 text-nowrap fs-sm fw-semi-bold'}
                    m={1}
                    height={'24px'}
                    outlined={true}
                    variant={theme.primaryBlue}
                    text={backToListText}
                    href={ModuleRouteUrls[moduleName]}
                    onClick={onBackToList}/>}

                {/* prev-button */}
                {(isBoolTrue(showNext)) && <ThemedIconButton
                    className={'p-1 text-nowrap fs-sm fw-semi-bold'}
                    m={1}
                    ml={2}
                    size={'24px'}
                    outlined={true}
                    variant={theme.primaryBlue}
                    disableVariant={'xl'}
                    icon={"bi bi-arrow-left"}
                    tooltip={"Previous"}
                    circular={false}
                    disabled={!checkNullStr(prevRecordId) || (isDataLoading || !isDataFound)}
                    href={showAs == ShowViewType.PAGE ? `${internalPageUrl}${prevRecordId}` : ''}
                    onClick={onPrev}/>}

                {/* next-button */}
                {(isBoolTrue(showPrev)) && <ThemedIconButton
                    className={'p-1 fs-sm'}
                    m={1}
                    ml={2}
                    size={'24px'}
                    outlined={true}
                    variant={theme.primaryBlue}
                    disableVariant={'xl'}
                    icon={"bi bi-arrow-right"}
                    tooltip={"Next"}
                    circular={false}
                    disabled={!checkNullStr(nextRecordId) || (isDataLoading || !isDataFound)}
                    href={showAs == ShowViewType.PAGE ? `${internalPageUrl}${nextRecordId}` : ''}
                    onClick={onNext}/>}

                {/* share-button */}
                {(canShowShareButton()) && <div className={''}>
                    <ThemedIconButton
                        className={'p-1 fs-sm'}
                        m={1}
                        ml={2}
                        size={'24px'}
                        outlined={true}
                        variant={theme.primaryBlue}
                        disabled={isDataLoading || !isDataFound}
                        disableVariant={'xl'}
                        icon={"fa fa-share"}
                        tooltip={"Share"}
                        circular={false}
                        onClick={() => {
                            updateField(Fields.showShareDialog, true);
                        }}
                    />
                </div>}
            </Flexbox>
    }

    function createSharePanel() {
        return <PageLinkSharePanel
            open={isFieldTrue(Fields.showShareDialog)}
            customShareUrl={shareUrl}
            onHide={() => updateField(Fields.showShareDialog, false)}
            onSuccessAndHide={() => updateField(Fields.showShareDialog, false)}
        />
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function canShowShareButton() {
        return (isCurrUserRoleAdmin() && isBoolTrue(showShare));
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }

    //======================Util Functions end=====================
});
export default InternalPageRoot;
